import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ChatwootWidget } from "@/components/ChatwootWidget";
import { useImpersonation } from "@/hooks/useImpersonation";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import { Skeleton } from "@/components/ui/skeleton";

import Auth from "./pages/Auth";
import Signup from "./pages/Signup";
import BoxAnalyzer from "./pages/BoxAnalyzer";
import BoxAnalyzerResults from "./pages/BoxAnalyzerResults";
import QRCodeRouter from "./pages/QRCodeRouter";
import StorageOnboardingBridge from "./pages/StorageOnboardingBridge";
import GuidedStorageOnboarding from "./pages/GuidedStorageOnboarding";
import StorageOnboardingWelcome from "./pages/StorageOnboardingWelcome";
import ExpressPickupOnboarding from "./pages/ExpressPickupOnboarding";
import DashboardConnected from "./pages/DashboardConnected";
import DashboardPreview from "./pages/DashboardPreview";
import BoxNewConnected from "./pages/BoxNewConnected";
import BoxLog from "./pages/BoxLog";
import BoxDetailConnected from "./pages/BoxDetailConnected";
import SuppliesStore from "./pages/SuppliesStore";
import SchedulePickupConnected from "./pages/SchedulePickupConnected";
import SchedulePickupSuccess from "./pages/SchedulePickupSuccess";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import Onboarding from "./pages/Onboarding";
import Admin from "./pages/Admin";
import AdminStripeProducts from "./pages/AdminStripeProducts";
import AdminAPISettings from "./pages/AdminAPISettings";
import AdminEmailTesting from "./pages/AdminEmailTesting";
import AdminMigrateBoxes from "./pages/AdminMigrateBoxes";
import AdminAddPrepaid from "./pages/AdminAddPrepaid";
import AdminUsers from "./pages/AdminUsers";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminReturnRequests from "./pages/AdminReturnRequests";
import AdminQRSources from "./pages/AdminQRSources";
import AdminFacilityScan from "./pages/AdminFacilityScan";
import SourceRouter from "./pages/SourceRouter";

// Lazy load documentation pages for better performance
const DocsLayout = lazy(() => import("./pages/docs/DocsLayout"));
const DocsOverviewPage = lazy(() => import("./pages/docs/DocsOverviewPage"));
const DocsJourneysPage = lazy(() => import("./pages/docs/DocsJourneysPage"));
const DocsEmailsPage = lazy(() => import("./pages/docs/DocsEmailsPage"));
const DocsChangelogPage = lazy(() => import("./pages/docs/DocsChangelogPage"));

const DocsLoadingFallback = () => (
  <div className="container max-w-5xl mx-auto px-4 py-8 space-y-4">
    <Skeleton className="h-10 w-48" />
    <Skeleton className="h-8 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const { isImpersonating, targetEmail, endImpersonation } = useImpersonation();
  
  return (
    <>
      <Toaster />
      <Sonner />
      {isImpersonating && targetEmail && (
        <ImpersonationBanner 
          targetUserEmail={targetEmail} 
          onExit={endImpersonation}
        />
      )}
      <Routes>
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/try" element={<BoxAnalyzer />} />
          <Route path="/try/results/:sessionId" element={<BoxAnalyzerResults />} />
          <Route path="/qr/:boxNumber" element={<QRCodeRouter />} />
          <Route path="/scan/:sourceCode" element={<SourceRouter />} />
          <Route path="/onboarding/storage" element={<StorageOnboardingBridge />} />
          <Route
            path="/onboarding/welcome"
            element={
              <ProtectedRoute>
                <StorageOnboardingWelcome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/express-pickup"
            element={
              <ProtectedRoute>
                <ExpressPickupOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/guided"
            element={
              <ProtectedRoute>
                <GuidedStorageOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/box/new"
            element={
              <ProtectedRoute>
                <BoxNewConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/box/log"
            element={
              <ProtectedRoute>
                <BoxLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/supplies"
            element={<SuppliesStore />}
          />
          <Route
            path="/dashboard-preview"
            element={
              <ProtectedRoute>
                <DashboardPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/box/:id"
            element={
              <ProtectedRoute>
                <BoxDetailConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule-pickup"
            element={
              <ProtectedRoute>
                <SchedulePickupConnected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule-pickup/success"
            element={
              <ProtectedRoute>
                <SchedulePickupSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout/success"
            element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <Admin />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stripe-products"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminStripeProducts />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/api-settings"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminAPISettings />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-testing"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminEmailTesting />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/migrate-boxes"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminMigrateBoxes />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          {/* Redirect old route */}
          <Route
            path="/admin/migrate-shopify-boxes"
            element={<Navigate to="/admin/migrate-boxes" replace />}
          />
          <Route
            path="/admin/add-prepaid"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminAddPrepaid />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminUsers />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminUserDetail />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/return-requests"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminReturnRequests />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/qr-sources"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminQRSources />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/facility-scanner"
            element={
              <ProtectedRoute>
                <ProtectedAdminRoute>
                  <AdminFacilityScan />
                </ProtectedAdminRoute>
              </ProtectedRoute>
            }
          />
          {/* Documentation routes with lazy loading */}
          <Route path="/docs" element={
            <Suspense fallback={<DocsLoadingFallback />}>
              <DocsLayout />
            </Suspense>
          }>
            <Route index element={<Navigate to="/docs/overview" replace />} />
            <Route path="overview" element={
              <Suspense fallback={<DocsLoadingFallback />}>
                <DocsOverviewPage />
              </Suspense>
            } />
            <Route path="journeys" element={
              <Suspense fallback={<DocsLoadingFallback />}>
                <DocsJourneysPage />
              </Suspense>
            } />
            <Route path="emails" element={
              <Suspense fallback={<DocsLoadingFallback />}>
                <DocsEmailsPage />
              </Suspense>
            } />
            <Route path="changelog" element={
              <Suspense fallback={<DocsLoadingFallback />}>
                <DocsChangelogPage />
              </Suspense>
            } />
          </Route>
          {/* Redirect old architecture route */}
          <Route path="/docs/architecture" element={<Navigate to="/docs/overview" replace />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      <ChatwootWidget />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
